import React, { useEffect, useState } from "react"
import { Button } from "@mui/material"
import { useLocation, useNavigate } from "react-router-dom"
import Swal from "sweetalert2"
import { useMutation } from "react-query"
import CvSkillDto, {
	LocationState,
	PoleLoisirInteretType,
} from "../../api/models/cvskill"
import cvskillService from "../../api/services/cvskillService"

interface PoleLoisirInteret {
	id?: number
	name: string
	type: PoleLoisirInteretType
}

function PoleLoisirsInterets() {
	const [selectedItems, setSelectedItems] = useState<PoleLoisirInteret[]>([])
	const [error, setError] = useState<string | null>(null)
	const navigate = useNavigate()
	const location = useLocation()
	const state = location.state as LocationState
	const [userId, setUserId] = useState<number | null>(null)
	const [cvSkillId, setCvSkillId] = useState<number | null>(null)
	const { editMode } = state

	const allItems = [
		// Sports
		{ name: "Sports de balle", type: PoleLoisirInteretType.SPORT },
		{ name: "Sports de raquettes", type: PoleLoisirInteretType.SPORT },
		{ name: "Sports de Combats", type: PoleLoisirInteretType.SPORT },
		{ name: "Sports cyclistes", type: PoleLoisirInteretType.SPORT },
		{ name: "Sports équestres", type: PoleLoisirInteretType.SPORT },
		{ name: "Sports gymniques", type: PoleLoisirInteretType.SPORT },
		{ name: "Sports athlétiques", type: PoleLoisirInteretType.SPORT },
		{ name: "Sports de glace", type: PoleLoisirInteretType.SPORT },
		{ name: "Sports de glisse", type: PoleLoisirInteretType.SPORT },
		// Intérêts
		{ name: "Arts plastiques", type: PoleLoisirInteretType.INTERET },
		{ name: "Cinéma", type: PoleLoisirInteretType.INTERET },
		{ name: "Cirque", type: PoleLoisirInteretType.INTERET },
		{ name: "Danse", type: PoleLoisirInteretType.INTERET },
		{ name: "Fête", type: PoleLoisirInteretType.INTERET },
		{ name: "Internet", type: PoleLoisirInteretType.INTERET },
		{ name: "Jeux", type: PoleLoisirInteretType.INTERET },
		{ name: "Lecture", type: PoleLoisirInteretType.INTERET },
		{ name: "Musique", type: PoleLoisirInteretType.INTERET },
		{ name: "Parcs de loisirs", type: PoleLoisirInteretType.INTERET },
		{ name: "Photographie", type: PoleLoisirInteretType.INTERET },
		{ name: "Radio", type: PoleLoisirInteretType.INTERET },
		{ name: "Télévision", type: PoleLoisirInteretType.INTERET },
		{ name: "Théâtre", type: PoleLoisirInteretType.INTERET },
		{ name: "Tourisme", type: PoleLoisirInteretType.INTERET },
		{ name: "Sport", type: PoleLoisirInteretType.INTERET },
		{ name: "Jeux vidéo", type: PoleLoisirInteretType.INTERET },
	]

	useEffect(() => {
		const fetchData = async () => {
			try {
				// Récupération de l'ID utilisateur
				const storedUserId =
					state?.userId || localStorage.getItem("selectedUserId")
				if (storedUserId) {
					setUserId(Number(storedUserId))
				} else {
					throw new Error("ID utilisateur non trouvé")
				}

				// Récupération de l'ID CV Skill
				const storedCvSkillId =
					state?.cvSkillId ||
					localStorage.getItem("selectedCvSkillId")
				if (storedCvSkillId) {
					setCvSkillId(Number(storedCvSkillId))
				}

				// Chargement des données existantes
				if (cvSkillId) {
					const cvSkill =
						await cvskillService.getCvSkillById(cvSkillId)
					if (cvSkill?.poleLoisirInterets) {
						setSelectedItems(cvSkill.poleLoisirInterets)
					}
				} else if (userId) {
					const storedItems = localStorage.getItem(
						`poleLoisirInterets-${userId}`,
					)
					if (storedItems) {
						setSelectedItems(JSON.parse(storedItems))
					}
				}
			} catch (error) {
				setError(
					"Impossible de récupérer les données. Veuillez réessayer.",
				)
			}
		}

		fetchData()
	}, [state, editMode, cvSkillId, userId])

	const handleChange = (item: PoleLoisirInteret) => {
		setSelectedItems(prev => {
			const isItemSelected = prev.some(
				i => i.name === item.name && i.type === item.type,
			)
			let updated: PoleLoisirInteret[]

			if (isItemSelected) {
				updated = prev.filter(
					i => !(i.name === item.name && i.type === item.type),
				)
			} else {
				const sameTypeCount = prev.filter(
					i => i.type === item.type,
				).length
				if (sameTypeCount >= 3) {
					setError(
						"Vous ne pouvez sélectionner que 3 choix maximum par catégorie.",
					)
					return prev
				}
				updated = [...prev, item]
			}

			setError(null)
			return updated
		})
	}

	const updateCvSkillMutation = useMutation<
		CvSkillDto,
		Error,
		{ id: number; cvSkillDto: CvSkillDto; userId: number }
	>(
		({ id, cvSkillDto, userId }) =>
			cvskillService.updateCvSkill(id, cvSkillDto, userId),
		{
			onSuccess: () => {
				Swal.fire({
					icon: "success",
					title: "Loisirs et intérêts mis à jour avec succès",
					showConfirmButton: false,
					timer: 1500,
				})
				navigate("/cvskill/cv", {
					state: { userId, cvSkillId },
				})
			},
			onError: () => {
				setError("Erreur lors de la mise à jour. Veuillez réessayer.")
			},
		},
	)

	const handleSubmit = async () => {
		if (!userId) {
			setError("ID utilisateur manquant. Impossible de continuer.")
			return
		}

		if (selectedItems.length === 0) {
			setError(
				"Veuillez sélectionner au moins un sport ou un centre d'intérêt.",
			)
			return
		}

		if (editMode && cvSkillId) {
			try {
				const currentCvSkill =
					await cvskillService.getCvSkillById(cvSkillId)
				const updatedCvSkill: CvSkillDto = new CvSkillDto({
					...currentCvSkill,
					poleLoisirInterets: selectedItems,
				})
				updateCvSkillMutation.mutate({
					id: cvSkillId,
					cvSkillDto: updatedCvSkill,
					userId,
				})
			} catch (error) {
				setError("Erreur lors de la mise à jour. Veuillez réessayer.")
			}
		} else {
			localStorage.setItem(
				`poleLoisirInterets-${userId}`,
				JSON.stringify(selectedItems),
			)
			navigate("/cvskill/polePersonnalite", {
				state: {
					...state,
					poleLoisirInterets: selectedItems,
				},
			})
		}
	}

	return (
		<div className="container mx-auto p-4 max-w-4xl relative overflow-y-auto h-[calc(100vh-8rem)] pb-16">
			<style>{`
                .container::-webkit-scrollbar {
                    display: none;
                }
                .container {
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }
                
                .selection-card {
                    position: relative;
                    background: white;
                    border: 2px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 16px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                }
                
                .selection-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(45, 175, 142, 0.2);
                    border-color: #7dd4c1;
                }
                
                .selection-card.selected {
                    background: linear-gradient(135deg, #d4f5ed 0%, #b8ede0 100%);
                    border-color: #2daf8e;
                    box-shadow: 0 4px 12px rgba(45, 175, 142, 0.3);
                }
                
                .selection-checkbox {
                    appearance: none;
                    width: 24px;
                    height: 24px;
                    border: 2px solid #d1d5db;
                    border-radius: 6px;
                    cursor: pointer;
                    position: relative;
                    transition: all 0.2s ease;
                    flex-shrink: 0;
                }
                
                .selection-checkbox:hover {
                    border-color: #2daf8e;
                }
                
                .selection-checkbox:checked {
                    background-color: #2daf8e;
                    border-color: #2daf8e;
                }
                
                .selection-checkbox:checked::after {
                    content: "✓";
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    color: white;
                    font-size: 16px;
                    font-weight: bold;
                }
                
                .section-title {
                    background: linear-gradient(135deg, #2daf8e 0%, #238f74 100%);
                    color: white;
                    padding: 12px 20px;
                    border-radius: 10px;
                    font-size: 20px;
                    font-weight: 600;
                    text-align: center;
                    box-shadow: 0 4px 6px rgba(45, 175, 142, 0.2);
                    margin-bottom: 20px;
                }
            `}</style>

			<h2
				className="text-2xl font-bold text-center mb-6"
				style={{ color: "#2daf8e" }}
			>
				{editMode
					? "Modifier vos loisirs et intérêts"
					: "Pôle Loisirs et Centres d'Intérêts"}
			</h2>
			{error && (
				<p className="text-red-500 text-center bg-red-50 p-3 rounded-lg mb-4">
					{error}
				</p>
			)}

			<div className="flex flex-col md:flex-row justify-between gap-6">
				{[
					PoleLoisirInteretType.SPORT,
					PoleLoisirInteretType.INTERET,
				].map(type => (
					<div key={type} className="w-full md:w-1/2">
						<div className="section-title">
							{type === PoleLoisirInteretType.SPORT
								? "⚽ Sports"
								: "🎨 Centres d'Intérêts"}
						</div>
						<div className="grid grid-cols-1 gap-3">
							{allItems
								.filter(item => item.type === type)
								.map(item => {
									const isSelected = selectedItems.some(
										i =>
											i.name === item.name &&
											i.type === item.type,
									)
									return (
										<div
											key={item.name}
											className={`selection-card ${isSelected ? "selected" : ""}`}
											onClick={() => handleChange(item)}
										>
											<label
												htmlFor={item.name}
												className="flex items-center space-x-3 cursor-pointer"
											>
												<input
													type="checkbox"
													id={item.name}
													name={item.name}
													checked={isSelected}
													onChange={() => {}}
													className="selection-checkbox"
												/>
												<span className="text-gray-800 font-medium text-base">
													{item.name}
												</span>
											</label>
										</div>
									)
								})}
						</div>
					</div>
				))}
			</div>

			<p className="mt-6 font-bold text-center text-red-500 bg-red-50 p-3 rounded-lg">
				⚠️ 3 choix maximum par catégorie
			</p>

			<div className="flex justify-center mt-8">
				<Button
					onClick={handleSubmit}
					variant="contained"
					color="primary"
					className="w-full max-w-md mt-6 py-3 px-6"
					disabled={selectedItems.length === 0}
					style={{
						background:
							selectedItems.length === 0
								? "#d1d5db"
								: "linear-gradient(135deg, #2daf8e 0%, #238f74 100%)",
						boxShadow:
							selectedItems.length === 0
								? "none"
								: "0 4px 12px rgba(45, 175, 142, 0.3)",
						textTransform: "none",
						fontSize: "16px",
						fontWeight: "600",
					}}
				>
					{editMode
						? "✓ Mettre à jour les loisirs et intérêts"
						: "Continuer →"}
				</Button>
			</div>
		</div>
	)
}

export default PoleLoisirsInterets
